import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Close as CloseIcon } from "@mui/icons-material";
import { Dialog, Tooltip, DialogContent, Divider } from "@mui/material";

import "components/pages/transmissions/modal/patient/PatientModal.css";

import Loading from "components/common/loading/Loading";
import PatientProfileTabs from "./tabs/PatientProfileTabs";
import usePatientModal from "components/pages/transmissions/modal/patient/usePatientModal";

import {
  changePatient,
  loadPatient,
  changeProfileTab,
  loadPatientReports,
  setAccessToProfileTabs,
  setUpdatedPatientNotes,
} from "app/store/transmission/slice/transmissionSlice";
import { useGetPatientQuery } from "app/store/transmission/api/transmissionTableApi";
import { EVOKE_ROLES, USER_ROLES } from "static/constants";
import AlertBanner from "../detail/alert/AlertBanner";
import PatientProfileTabContent from "./tabs/PatientProfileTabContent";
import UnsavedChangesModal from "components/common/modals/unsavedChangesModal/UnsavedChangesModal";
import useUnsavedChanges from "hooks/useUnsavedChanges";

const PatientModal = () => {
  const {
    dispatch,
    handlePatientModalClose,
    reportDetail,
    isPatientModal,
    patientAccount,
    patientSerialNumber,
    isReportDetailModal,
    patientId,
  } = usePatientModal();
  const { currentUser } = useSelector((state) => state.auth);
  const { profile, hasAccessToProfileTabs, patientCommunicationAndNotes } =
    useSelector((state) => state.transmission);

  const initializedSerialRef = useRef(null);

  const hasUnsavedNotes = !!patientCommunicationAndNotes?.updatedNotes;
  const { unsavedChanges, requestClose, discardAndLeave, keepEditing } =
    useUnsavedChanges({
      isOpen: isPatientModal,
      onClose: () => {
        dispatch(setUpdatedPatientNotes({ note: "" }));
        handlePatientModalClose();
      },
      hasUnsavedChanges: hasUnsavedNotes,
      getUnsavedChanges: () =>
        hasUnsavedNotes ? ["Communication and Notes History"] : [],
    });

  const { isLoading, isFetching, data } = useGetPatientQuery(
    {
      patientId,
      isPatientModal,
    },
    { refetchOnMountOrArgChange: true }
  );

  useEffect(() => {
    if (data?.serial_number && !patientSerialNumber) {
      dispatch(
        changePatient({
          patientId,
          serialNumber: data.serial_number,
          account: data.account,
        })
      );
    }
  }, [data?.serial_number, data?.account, patientSerialNumber, patientId]);

  useEffect(() => {
    if (data) {
      dispatch(loadPatient(data));
      dispatch(loadPatientReports(data?.reports));

      const isCurrentUserAuthorized =
        [...EVOKE_ROLES, USER_ROLES.CLINIC_ADMIN]?.includes(
          currentUser?.role
        ) && currentUser?.accounts?.includes(data?.account);

      const isSameAccount =
        data?.account ===
        (isReportDetailModal ? reportDetail?.account : patientAccount);

      const hasAccessAllTabs =
        isSameAccount || (!isSameAccount && isCurrentUserAuthorized);
      dispatch(setAccessToProfileTabs(hasAccessAllTabs));

      // Only set the initial tab the first time this patient loads
      if (initializedSerialRef.current !== patientSerialNumber) {
        const initialTab = hasAccessAllTabs ? "generalInformation" : "reports";
        dispatch(changeProfileTab(initialTab));
        initializedSerialRef.current = patientSerialNumber;
      }
    }
  }, [
    data,
    dispatch,
    currentUser?.role,
    currentUser?.accounts,
    reportDetail?.account,
    isReportDetailModal,
    patientAccount,
    patientSerialNumber,
  ]);

  return (
    <div>
      <Dialog
        fullScreen
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        open={isPatientModal}
        onClose={() => requestClose()}
        scroll="paper"
        className="patient-modal__dialog"
      >
        <>
          <div className="patient-modal__header" onClick={() => requestClose()}>
            <div className="patient-modal__header-text">
              Patient Information
            </div>
            <Tooltip title="Close" placement="bottom">
              <div className="patient-modal__close">
                <CloseIcon />
              </div>
            </Tooltip>
          </div>

          <Divider />

          {(!isLoading || !isFetching) && <PatientProfileTabs />}
        </>
        <DialogContent style={{ backgroundColor: "#f5f5f5" }} dividers>
          {isLoading || isFetching ? (
            <Loading height="500px" />
          ) : (
            <>
              {hasAccessToProfileTabs && (
                <AlertBanner serialNumber={profile?.serial_number} />
              )}
              <PatientProfileTabContent />
            </>
          )}
        </DialogContent>
      </Dialog>

      {unsavedChanges && (
        <UnsavedChangesModal
          changes={unsavedChanges}
          onCancel={keepEditing}
          onConfirm={discardAndLeave}
        />
      )}
    </div>
  );
};

export default PatientModal;
